/**
 * Custom calendar builder: product set-up from size / orientation / calendar
 * type, the layout grid blocks snap to, and adding / removing blocks.
 * All geometry is in millimetres (trimmed page, top-left origin).
 */
import { K_BLACK, rgb } from './color.js';
import { SHEET_SIZES } from './units.js';
import { pageGeometry, setElementProp, sheetSize, type DesignKey } from './project.js';
import { dlTentProduct, type TentLayoutKind } from './tent.js';
import { buildTemplate, scaleDesign, templateInfo, ts } from './templates.js';
import type { Element, LayoutGrid, PageKey, Product, ProjectDoc, Rect, SavedTemplate } from './schema.js';

export const BUILDER_SIZES = ['A1', 'A2', 'A3', 'A4', 'A5', 'DL'] as const;
export type BuilderSize = (typeof BUILDER_SIZES)[number];
/** Year view (1 page), 6-month view (two faces of a tent, head-to-head on one sheet) or monthly (12 pages). */
export type CalendarKind = 'year' | 'half' | 'monthly';

export interface BuilderSpec {
  size: BuilderSize;
  orientation: 'portrait' | 'landscape';
  kind: CalendarKind;
  /** monthly: add a cover page (13 pages) */
  cover: boolean;
  /** DL tent layout (kind "half" at DL size) */
  tent?: Exclude<TentLayoutKind, 'classic'>;
}

/** Product settings and blank starting template for a builder choice. */
export function customProduct(spec: BuilderSpec): { templateId: string; product: Partial<Product> } {
  const s = SHEET_SIZES[spec.size];
  const page = spec.orientation === 'portrait' ? { w: s.w, h: s.h } : { w: s.h, h: s.w };
  if (spec.kind === 'half' && spec.size === 'DL')
    // DL tents: three 99 mm panels and a 15 mm tape strip (landscape tent or portrait tower)
    return { templateId: 'custom-half', product: dlTentProduct(spec.tent ?? (spec.orientation === 'landscape' ? 'dl-landscape' : 'dl-portrait-6')) };
  if (spec.kind === 'half')
    // one flat sheet: face B (Jul–Dec, turned 180°) above face A (Jan–Jun); fold in the middle
    return {
      templateId: 'custom-half',
      product: {
        productType: 'tent', sheetPreset: 'custom', orientation: page.w >= 2 * page.h ? 'landscape' : 'portrait',
        widthMm: page.w, heightMm: 2 * page.h, tent: { baseMm: 0, tabMm: 0, layout: 'classic' },
        bindingEdge: 'none', bindingClearanceMm: 0, includeCover: false, duplex: false, calendarArea: 'full', cropMarks: true,
        finishing: `${spec.size} tent: two ${page.w} × ${page.h} mm faces printed head-to-head; score and fold in the middle.`,
      },
    };
  if (spec.kind === 'year')
    return {
      templateId: 'custom-year',
      product: { productType: 'year', sheetPreset: spec.size, orientation: spec.orientation, bindingEdge: 'none', bindingClearanceMm: 0, includeCover: false, duplex: false, calendarArea: 'full' },
    };
  return {
    templateId: 'custom-month',
    product: { productType: 'monthly', sheetPreset: spec.size, orientation: spec.orientation, includeCover: spec.cover, duplex: false, calendarArea: 'full' },
  };
}

// ---------------------------------------------------------------- layout grid

export interface GridSpec {
  /** area the grid covers: the safe area minus binding / rim clearance */
  area: Rect;
  cols: number;
  rows: number;
  gutter: number;
  cellW: number;
  cellH: number;
}

/** The layout grid of a page. */
export function layoutGridSpec(doc: ProjectDoc, key: PageKey, grid: LayoutGrid = doc.layoutGrid): GridSpec {
  const g = pageGeometry(doc, key);
  let { x, y, w, h } = g.safe;
  // clearance bands across the full width / height shrink the grid area (a pad zone inside the page does not)
  for (const z of g.bindings) {
    const fullW = z.x <= 0.01 && z.w >= g.trimW - 0.01, fullH = z.y <= 0.01 && z.h >= g.trimH - 0.01;
    if (fullW && z.y <= 0.01) { const b = z.y + z.h; if (b > y) { h -= b - y; y = b; } }
    else if (fullW) { const t = z.y; if (t < y + h) h = t - y; }
    else if (fullH && z.x <= 0.01) { const r = z.x + z.w; if (r > x) { w -= r - x; x = r; } }
  }
  const gutter = Math.min(grid.gutterMm, w / grid.cols / 2, h / grid.rows / 2);
  const cellW = (w - (grid.cols - 1) * gutter) / grid.cols;
  const cellH = (h - (grid.rows - 1) * gutter) / grid.rows;
  return { area: { x, y, w, h }, cols: grid.cols, rows: grid.rows, gutter, cellW, cellH };
}

export function gridCells(spec: GridSpec): Rect[] {
  const out: Rect[] = [];
  for (let r = 0; r < spec.rows; r++)
    for (let c = 0; c < spec.cols; c++)
      out.push({ x: spec.area.x + c * (spec.cellW + spec.gutter), y: spec.area.y + r * (spec.cellH + spec.gutter), w: spec.cellW, h: spec.cellH });
  return out;
}

const round = (v: number) => +v.toFixed(2);
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/** Cells spanned by a rectangle, rounded to whole cells: { c0, c1, r0, r1 } inclusive. */
function axis(start: number, size: number, origin: number, cell: number, gutter: number, count: number, keepSpan: boolean) {
  const pitch = cell + gutter;
  if (keepSpan) {
    const span = clamp(Math.round((size + gutter) / pitch), 1, count);
    const i0 = clamp(Math.round((start - origin) / pitch), 0, count - span);
    return { i0, i1: i0 + span - 1 };
  }
  const i0 = clamp(Math.round((start - origin) / pitch), 0, count - 1);
  const i1 = clamp(Math.round((start + size - origin + gutter) / pitch) - 1, i0, count - 1);
  return { i0, i1 };
}

/**
 * Snap a rectangle to whole grid cells.
 * - move: keeps the number of cells spanned and snaps the position
 * - resize: snaps each edge to the nearest cell edge (at least one cell)
 */
export function snapToGrid(rect: Rect, spec: GridSpec, mode: 'move' | 'resize'): Rect {
  const keep = mode === 'move';
  const cx = axis(rect.x, rect.w, spec.area.x, spec.cellW, spec.gutter, spec.cols, keep);
  const cy = axis(rect.y, rect.h, spec.area.y, spec.cellH, spec.gutter, spec.rows, keep);
  return cellRect(spec, cx.i0, cy.i0, cx.i1 - cx.i0 + 1, cy.i1 - cy.i0 + 1);
}

/** Rectangle covering cols × rows cells from cell (col, row). */
export function cellRect(spec: GridSpec, col: number, row: number, cols = 1, rows = 1): Rect {
  const pw = spec.cellW + spec.gutter, ph = spec.cellH + spec.gutter;
  return { x: round(spec.area.x + col * pw), y: round(spec.area.y + row * ph), w: round(cols * pw - spec.gutter), h: round(rows * ph - spec.gutter) };
}

/**
 * Where a new block of cols × rows cells goes: the first position (reading
 * order) that overlaps no other block. The calendar itself is ignored, since
 * in a blank design it fills the page until it is resized.
 */
export function freeCellRect(spec: GridSpec, cols: number, rows: number, elements: Element[]): Rect {
  cols = clamp(cols, 1, spec.cols);
  rows = clamp(rows, 1, spec.rows);
  const others = elements.filter((e) => !e.hidden && e.type !== 'grid' && e.type !== 'yeargrid').map((e) => e.rect);
  const hits = (r: Rect) => others.some((o) => r.x < o.x + o.w - 0.01 && r.x + r.w > o.x + 0.01 && r.y < o.y + o.h - 0.01 && r.y + r.h > o.y + 0.01);
  for (let row = 0; row + rows <= spec.rows; row++)
    for (let col = 0; col + cols <= spec.cols; col++) {
      const r = cellRect(spec, col, row, cols, rows);
      if (!hits(r)) return r;
    }
  return cellRect(spec, 0, 0, cols, rows);
}

// ---------------------------------------------------------------- blocks

export type BlockKind = 'image' | 'logo' | 'text' | 'panel' | 'mini-prev' | 'mini-next' | 'key' | 'holidays';
export const BLOCK_LABELS: Record<BlockKind, string> = {
  image: 'Image (photo)', logo: 'Logo', text: 'Text', panel: 'Colour panel', 'mini-prev': 'Previous month (mini)', 'mini-next': 'Next month (mini)', key: 'Holiday colour key', holidays: 'Holiday & event list',
};

/** Pixels needed for a frame at a print resolution (default 300 ppi). */
export function requiredPixels(rect: Pick<Rect, 'w' | 'h'>, ppi = 300) {
  return { w: Math.ceil((rect.w / 25.4) * ppi), h: Math.ceil((rect.h / 25.4) * ppi) };
}

function uniqueId(elements: Element[], base: string) {
  const ids = new Set(elements.map((e) => e.id));
  for (let i = 1; ; i++) if (!ids.has(`${base}-${i}`)) return `${base}-${i}`;
}

/** A new block of the given kind at rect. */
export function makeBlock(kind: BlockKind, rect: Rect, elements: Element[]): Element {
  const pt = (mm: number) => Math.max(3.5, +(mm * (72 / 25.4)).toFixed(1));
  switch (kind) {
    case 'image': {
      const slot = elements.reduce((m, e) => (e.type === 'image' ? Math.max(m, e.slot + 1) : m), 0);
      const n = elements.filter((e) => e.type === 'image').length + 1;
      return { id: uniqueId(elements, 'image'), name: `Image ${n}`, type: 'image', slot, rect, radiusMm: 0, border: null, required: false };
    }
    case 'logo':
      return { id: uniqueId(elements, 'logo'), name: 'Logo', type: 'logo', rect, align: 'center' };
    case 'text':
      return { id: uniqueId(elements, 'text'), name: 'Text', type: 'text', role: 'custom', rect, text: 'Your text', style: ts('inter-600', pt(Math.min(rect.h * 0.45, 12)), K_BLACK, 'center'), valign: 'middle', panel: null };
    case 'panel':
      return { id: uniqueId(elements, 'panel'), name: 'Colour panel', type: 'shape', rect, fill: rgb('#eef1f5'), opacity: 1, radiusMm: 0, stroke: null };
    case 'mini-prev':
    case 'mini-next': {
      const which = kind === 'mini-prev' ? 'prev' : 'next';
      const k = rect.w / 27;
      return { id: uniqueId(elements, `mini-${which}`), name: which === 'prev' ? 'Previous month' : 'Next month', type: 'mini', which, rect, title: ts('inter-700', Math.max(3, 5.5 * k), K_BLACK, 'center'), text: ts('inter-400', Math.max(3, 4.5 * k), rgb('#444444'), 'center') };
    }
    case 'holidays': {
      const landscape = rect.w > rect.h * 2.5;
      return {
        id: uniqueId(elements, 'holidays'), name: 'Holiday & event list', type: 'holidays', kinds: ['public'], rect,
        style: ts('inter-400', Math.max(3.5, Math.min(9, +((landscape ? rect.h / 3 : rect.h / 16) * (72 / 25.4)).toFixed(1))), rgb('#333333'), 'left', { lineHeight: 1.3 }),
        layout: landscape ? 'row' : 'column', columns: 1, dateFormat: 'numeric', names: 'short', range: 'year', observed: 'marker',
        marker: '*', separator: ' | ', footnote: '* Sunday public holiday observed on the Monday',
      };
    }
    case 'key':
      return { id: uniqueId(elements, 'key'), name: 'Holiday key', type: 'key', rect, style: ts('inter-400', pt(Math.min(rect.h * 0.4, 4)), rgb('#444444'), 'left'), align: 'center' };
  }
}

/** Blocks a calendar type offers (mini months only on month pages). */
export function blockKinds(design: DesignKey): BlockKind[] {
  return design === 'month' ? ['image', 'logo', 'text', 'panel', 'holidays', 'mini-prev', 'mini-next'] : ['image', 'logo', 'text', 'panel', 'holidays', 'key'];
}

/** Add a block to a design (all pages that share the design). */
export function addBlock(doc: ProjectDoc, design: DesignKey, el: Element): ProjectDoc {
  return { ...doc, designs: { ...doc.designs, [design]: { ...doc.designs[design], elements: [...doc.designs[design].elements, el] } } };
}

/** Remove a block from a design, with its per-page overrides and photos. */
export function removeBlock(doc: ProjectDoc, design: DesignKey, id: string): ProjectDoc {
  const el = doc.designs[design].elements.find((e) => e.id === id);
  if (!el) return doc;
  const overrides = Object.fromEntries(Object.entries(doc.overrides).map(([k, o]) => {
    if (!o.elements?.[id]) return [k, o];
    const elements = { ...o.elements };
    delete elements[id];
    return [k, { ...o, elements }];
  }));
  let photos = doc.photos;
  if (el.type === 'image') {
    // drop photo placements for this slot unless another frame still uses it
    const stillUsed = doc.designs[design].elements.some((e) => e.id !== id && e.type === 'image' && e.slot === el.slot);
    if (!stillUsed && design !== 'cover' && design !== 'backing')
      photos = Object.fromEntries(Object.entries(doc.photos).map(([k, slots]) => {
        const s = { ...slots };
        delete s[String(el.slot)];
        return [k, s];
      }));
  }
  return { ...doc, overrides, photos, designs: { ...doc.designs, [design]: { ...doc.designs[design], elements: doc.designs[design].elements.filter((e) => e.id !== id) } } };
}

/** Copy a block, offset by one grid cell (or 5 mm), as a new block. */
export function duplicateBlock(doc: ProjectDoc, design: DesignKey, id: string, offset: { x: number; y: number }): { doc: ProjectDoc; id: string | null } {
  const els = doc.designs[design].elements;
  const el = els.find((e) => e.id === id);
  if (!el) return { doc, id: null };
  const base = el.id.replace(/-\d+$/, '');
  const copy = structuredClone(el) as Element;
  copy.id = uniqueId(els, base);
  copy.name = `${el.name} (copy)`;
  copy.rect = { ...el.rect, x: round(el.rect.x + offset.x), y: round(el.rect.y + offset.y) };
  if (copy.type === 'image') copy.slot = els.reduce((m, e) => (e.type === 'image' ? Math.max(m, e.slot + 1) : m), 0);
  return { doc: addBlock(doc, design, copy), id: copy.id };
}

/** Rename a block (the name shows in the editor, issues and the template preview). */
export function renameBlock(doc: ProjectDoc, design: DesignKey, id: string, name: string): ProjectDoc {
  return setElementProp(doc, 'all', design, id, ['name'], name.slice(0, 80) || 'Block');
}

/** Move a block up/down the stacking order (front = drawn last). */
export function reorderBlock(doc: ProjectDoc, design: DesignKey, id: string, dir: 'front' | 'back' | 'forward' | 'backward'): ProjectDoc {
  const els = [...doc.designs[design].elements];
  const i = els.findIndex((e) => e.id === id);
  if (i < 0) return doc;
  const [el] = els.splice(i, 1);
  const j = dir === 'front' ? els.length : dir === 'back' ? 0 : dir === 'forward' ? Math.min(els.length, i + 1) : Math.max(0, i - 1);
  els.splice(j, 0, el);
  return { ...doc, designs: { ...doc.designs, [design]: { ...doc.designs[design], elements: els } } };
}

// ---------------------------------------------------------------- saved templates

const DESIGN_KEYS = ['month', 'cover', 'backing', 'year', 'face', 'carrier', 'info'] as const;

/** Save the current design (with product and layout grid) as a template. */
export function toSavedTemplate(doc: ProjectDoc): SavedTemplate {
  const product: Partial<Product> = { ...doc.product };
  delete product.catalogId;
  return { baseTemplateId: doc.templateId, size: { w: doc.product.widthMm, h: doc.product.heightMm }, designs: structuredClone(doc.designs), product, layoutGrid: { ...doc.layoutGrid } };
}

/** Calendar type of a saved template (older templates only had month designs). */
export function savedTemplateType(t: SavedTemplate): Product['productType'] {
  return t.product?.productType ?? templateInfo(t.baseTemplateId).product;
}

/**
 * Whether a saved template can be applied to a project: same calendar type,
 * and for builder templates the same finished size and orientation.
 */
export function savedTemplateFits(t: SavedTemplate, doc: ProjectDoc): boolean {
  if (savedTemplateType(t) !== doc.product.productType) return false;
  if (!t.product) return true; // older templates rescale
  return Math.abs(t.size.w - doc.product.widthMm) < 0.5 && Math.abs(t.size.h - doc.product.heightMm) < 0.5;
}

/** Apply a saved template: its designs, product settings and layout grid; photos, events and branding are kept. */
export function applySavedTemplate(doc: ProjectDoc, t: SavedTemplate): ProjectDoc {
  const product: Product = t.product ? { ...doc.product, ...t.product, catalogId: doc.product.catalogId } : doc.product;
  const size = sheetSize(product);
  product.widthMm = size.w;
  product.heightMm = size.h;
  const fresh = buildTemplate(t.baseTemplateId, size, product);
  const scale = (k: (typeof DESIGN_KEYS)[number]) => {
    const d = t.designs[k];
    if (!d) return fresh[k];
    // faces and carrier have their own sizes; page designs rescale only for older templates
    if (k === 'face' || k === 'carrier' || k === 'info' || (Math.abs(t.size.w - size.w) < 0.5 && Math.abs(t.size.h - size.h) < 0.5)) return structuredClone(d);
    return scaleDesign(d, t.size, size);
  };
  const designs = Object.fromEntries(DESIGN_KEYS.map((k) => [k, scale(k)])) as ProjectDoc['designs'];
  return { ...doc, templateId: t.baseTemplateId, product, designs, overrides: {}, layoutGrid: t.layoutGrid ? { ...t.layoutGrid } : doc.layoutGrid };
}

/** The block kind of an element, or null for the calendar itself (grid / year grid / legend). */
export function blockKindOf(el: Element): BlockKind | null {
  switch (el.type) {
    case 'image': return 'image';
    case 'logo': return 'logo';
    case 'text': return 'text';
    case 'shape': return 'panel';
    case 'mini': return el.which === 'prev' ? 'mini-prev' : 'mini-next';
    case 'key': return 'key';
    case 'holidays': return 'holidays';
    default: return null;
  }
}

/**
 * Turn a block into another kind (e.g. a text block into an image block),
 * keeping its position, size, stacking order and visibility. Settings that
 * belong to the old kind (and its photos / per-month overrides) are dropped.
 */
export function changeBlockKind(doc: ProjectDoc, design: DesignKey, id: string, kind: BlockKind): { doc: ProjectDoc; id: string } {
  const els = doc.designs[design].elements;
  const i = els.findIndex((e) => e.id === id);
  if (i < 0 || blockKindOf(els[i]) === kind || blockKindOf(els[i]) === null) return { doc, id };
  const old = els[i];
  const others = els.filter((e) => e.id !== id);
  const next = makeBlock(kind, old.rect, others);
  if (old.hidden) next.hidden = true;
  const removed = removeBlock(doc, design, id);
  const list = [...removed.designs[design].elements];
  list.splice(i, 0, next);
  return { doc: { ...removed, designs: { ...removed.designs, [design]: { ...removed.designs[design], elements: list } } }, id: next.id };
}

/** Add a block of a kind to a page's design in the first free grid cells; returns the new block id. */
export function addBlockToPage(doc: ProjectDoc, page: PageKey, design: DesignKey, kind: BlockKind, elementsOnPage: Element[]): { doc: ProjectDoc; id: string } {
  const spec = layoutGridSpec(doc, page);
  const cols = Math.min(spec.cols, kind === 'image' ? Math.max(2, Math.ceil(spec.cols / 2)) : 2);
  const rows = Math.min(spec.rows, kind === 'image' || kind === 'holidays' ? Math.max(2, Math.ceil(spec.rows / 3)) : 1);
  const rect = freeCellRect(spec, cols, rows, elementsOnPage);
  const el = makeBlock(kind, rect, doc.designs[design].elements);
  return { doc: addBlock(doc, design, el), id: el.id };
}
